<template>
  <div class="arvores">
    <div class="arvores__quadro">
      <!--
        O NÚCLEO: os pontos são a restrição comum das três árvores, como o ouro é o da roda (D19).
        Ficam aqui, uma vez, e não em cada nó — o nó mostra ícone, nível e se libera, e só isso.
      -->
      <div class="arvores__nucleo">
        <p class="arvores__pontos">
          <FishIcon nome="star" tamanho="medio" />
          <span class="t-numero arvores__pontos-valor">{{ pontos.disponiveis }}</span>
          <span class="t-legenda">{{ $t('skills.points.label') }}</span>
        </p>
        <p class="t-legenda arvores__alocados">
          {{ $t('skills.points.allocated', { alocados: pontos.alocados, total: pontos.total }) }}
        </p>
        <p class="t-legenda arvores__capstone">
          {{
            capstone
              ? $t('skills.capstone.chosen', { nome: $t(`skills.catalog.${capstone}.name`) })
              : $t('skills.capstone.none')
          }}
        </p>
      </div>

      <!-- Os três trilhos, um por árvore. Todos visíveis: a escolha do capstone é entre as três,
           e uma sub-aba por árvore esconderia justamente o que se está decidindo (D4). -->
      <div class="arvores__trilhos">
        <div v-if="carregando && !visao" class="arvores__carregando">
          <FishSkeleton forma="bloco" altura="520px" />
        </div>

        <div v-else class="arvores__pista">
          <section v-for="arvore in arvores" :key="arvore.id" class="arvores__arvore">
            <h3 class="t-legenda arvores__nome">
              {{ $t(`skills.trees.${arvore.id}`) }}
              <span class="t-numero arvores__nome-pontos">
                {{ arvore.pontos }}<span class="arvores__nome-total">/{{ arvore.custoTotal }}</span>
              </span>
            </h3>
            <FishNodeMap
              v-model="habilidadeAtiva"
              :nos="nosDa(arvore)"
              arranjo="trilho"
              :progresso="progressoDa(arvore)"
              :rotulo="$t(`skills.trees.${arvore.id}`)"
            />
          </section>
        </div>
      </div>

      <!-- O painel de detalhe e o reset. No desktop ficam grudados à esquerda enquanto os trilhos
           rolam; no telefone, logo abaixo deles. -->
      <div class="arvores__lado">
        <FishFrame v-if="ativa" :titulo="$t(`skills.catalog.${ativa.id}.name`)" class="arvores__detalhe">
          <p class="t-legenda arvores__arvore-da-ativa">{{ $t(`skills.trees.${ativa.arvore}`) }}</p>
          <p class="t-corpo arvores__descricao">{{ $t(`skills.catalog.${ativa.id}.description`) }}</p>
          <p class="t-legenda arvores__tier">
            {{ $t('skills.level', { nivel: ativa.nivel, maximo: ativa.nivelMaximo }) }} · {{ textoDoTier }}
          </p>

          <dl class="arvores__efeitos">
            <div class="arvores__efeito">
              <dt class="t-legenda">{{ $t('skills.effect.current') }}</dt>
              <dd class="t-numero">{{ ativa.efeitoAtual }}</dd>
            </div>
            <FishIcon nome="chevron-right" tamanho="pequeno" class="arvores__seta" />
            <div class="arvores__efeito">
              <dt class="t-legenda">{{ $t('skills.effect.next') }}</dt>
              <dd class="t-numero arvores__efeito-proximo">
                {{ ativa.efeitoProximo ?? $t('skills.effect.max') }}
              </dd>
            </div>
          </dl>

          <div class="acao-principal arvores__acao">
            <FishButton
              variante="primario"
              tamanho="grande"
              bloco
              som="compra"
              :desabilitado="!ativa.liberada"
              :motivo="ativa.liberada ? '' : motivoDoBloqueio"
              :carregando="alocando === ativa.id"
              @click="alocar(ativa.id)"
            >
              {{ $t('skills.allocate') }}
              <template v-if="ativa.custoDoProximo != null">
                · {{ ativa.custoDoProximo }}
                <FishIcon nome="star" tamanho="miudo" />
              </template>
            </FishButton>
          </div>
        </FishFrame>

        <!-- O reset é total, pago por ponto, com espera de um dia (D3). Fica longe do botão que
             gasta ponto: quem clica aqui está desfazendo tudo. -->
        <FishFrame v-if="visao" :titulo="$t('skills.reset.title')" class="arvores__reset">
          <p class="t-corpo arvores__descricao">{{ $t('skills.reset.description') }}</p>
          <p v-if="emCooldown" class="t-legenda arvores__cooldown">
            {{ $t('skills.reset.cooldown', { quando: quando(reset.disponivelEm) }) }}
          </p>
          <div class="arvores__acao">
            <FishButton
              variante="primario"
              bloco
              :desabilitado="pontos.alocados === 0 || emCooldown"
              :motivo="motivoDoReset"
              :carregando="resetando"
              @click="resetar"
            >
              {{ $t('skills.reset.action') }} · {{ formatar(reset.custo) }}
              <FishIcon nome="coins" tamanho="miudo" />
            </FishButton>
          </div>
        </FishFrame>
      </div>
    </div>

    <p v-if="erro" class="arvores__erro">{{ $t(`skills.errors.${erro}`) }}</p>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { FishNodeMap, FishFrame, FishButton, FishIcon, FishSkeleton } from '@/shared/ui';

/**
 * As árvores de habilidades (1.3) — a segunda aba das Melhorias (D11, D12).
 *
 * ## O que esta tela NÃO faz
 *
 * Não calcula nada. Os pontos, se um ponto entra, por que não, o efeito atual e o próximo — tudo
 * chega pronto do servidor na **visão**, e a store substitui a visão inteira a cada resposta. A
 * roda de melhorias já pagou o preço de derivar no cliente (`luck` 7,5 contra 4,5); aqui a
 * fórmula mora no `packages/domain` e o cliente só mostra.
 *
 * ## As três de uma vez
 *
 * A primeira versão tinha uma sub-aba por árvore. Estava errada: **só um capstone entre as três**
 * pode ser escolhido, e uma sub-aba esconde exatamente o que o jogador está decidindo. Os três
 * trilhos ficam empilhados; o núcleo com os pontos, à esquerda, é a restrição comum, e o painel
 * de detalhe é um só para as 36.
 *
 * ## O trilho
 *
 * `FishNodeMap` com `arranjo="trilho"`: dois trilhos paralelos correm da esquerda para a direita
 * com as oito habilidades de tier 1 (quatro colunas × duas fileiras), seguem para o par do tier 2
 * e **se juntam** no tier 3; o capstone é a última parada. A trilha enche com os pontos daquela
 * árvore. `bruma` é o tier que ainda não abriu; `pleno`, o nó que já está no máximo.
 *
 * ## Os ícones
 *
 * Reaproveitam o `pixel-icons.css`: nome fora do sprite renderiza vazio, **sem erro em lugar
 * nenhum** (é uma armadilha conhecida deste cliente), então cada um dos 36 aponta para um ícone
 * que já existe.
 */
const ICONE = {
  'pescador.maos_calejadas': 'fish-fins',
  'pescador.linha_reforcada': 'anchor',
  'pescador.mao_firme': 'ruler',
  'pescador.folego': 'battery-full',
  'pescador.madrugador': 'bolt',
  'pescador.olhar_de_aguia': 'eye',
  'pescador.aguas_profundas': 'water',
  'pescador.especialista_em_iscas': 'cais-isca',
  'pescador.lendario': 'star',
  'pescador.experiencia_de_vida': 'graduation-cap',
  'pescador.forca_bruta': 'crosshairs',
  'pescador.verdadeiro_pescador': 'crown',

  'explorador.senso_de_aventura': 'map',
  'explorador.piloto_licenciado': 'paper-plane',
  'explorador.rota_alta': 'level-up-alt',
  'explorador.garimpeiro': 'gem',
  'explorador.colecionador': 'cubes',
  'explorador.olho_do_navegador': 'search',
  'explorador.cartografo': 'map-marked-alt',
  'explorador.curioso': 'dice',
  'explorador.mestre_das_reliquias': 'box-open',
  'explorador.mecanico_de_bordo': 'cog',
  'explorador.voo_noturno': 'ticket-alt',
  'explorador.explorador_lendario': 'trophy',

  'magnata.negociador_nato': 'handshake',
  'magnata.gestor_de_equipe': 'users',
  'magnata.treinador': 'graduation-cap',
  'magnata.contabilidade': 'file-invoice-dollar',
  'magnata.armazem_organizado': 'warehouse',
  'magnata.promocao_relampago': 'level-up-alt',
  'magnata.magnata_dos_mares': 'hand-holding-usd',
  'magnata.desconto_corporativo': 'wallet',
  'magnata.lideranca': 'user-tie',
  'magnata.peixeiro': 'store',
  'magnata.turno_dobrado': 'clock',
  'magnata.senhor_do_imperio': 'building',
};

/**
 * Onde cada habilidade cai no trilho, pela **ordem** que o servidor manda (1 a 12).
 *
 * As oito de tier 1 abrem com zero pontos e não têm ordem entre si: ocupam as quatro primeiras
 * colunas nas duas fileiras. O par do tier 2 continua as duas fileiras; o tier 3 é a junção, e o
 * capstone, a ponta. A linha entre os nós é o TRILHO, não um pré-requisito — o que trava é o
 * tier, e quem diz isso é o servidor em `motivoBloqueio`.
 */
function posicaoNoTrilho(ordem) {
  if (ordem <= 4) return { coluna: ordem, fileira: 0 };
  if (ordem <= 8) return { coluna: ordem - 4, fileira: 1 };
  if (ordem <= 10) return { coluna: 5, fileira: ordem === 9 ? 0 : 1 };
  return { coluna: ordem === 11 ? 6 : 7, fileira: 0.5 };
}

export default {
  name: 'ArvoreDeHabilidades',
  components: { FishNodeMap, FishFrame, FishButton, FishIcon, FishSkeleton },
  data() {
    return {
      habilidadeAtiva: null,
      alocando: null,
      resetando: false,
      erro: null,
      agora: Date.now(),
    };
  },
  computed: {
    ...mapGetters('skills', ['visao', 'pontos', 'reset', 'capstone']),
    carregando() {
      return this.$store.getters['skills/isLoading'];
    },
    arvores() {
      return this.visao?.arvores ?? [];
    },
    /** A habilidade escolhida, com a árvore de onde ela veio — o painel é um só para as três. */
    ativa() {
      for (const arvore of this.arvores) {
        const habilidade = arvore.habilidades.find((h) => h.id === this.habilidadeAtiva);
        if (habilidade) return { ...habilidade, arvore: arvore.id };
      }
      return null;
    },
    textoDoTier() {
      const h = this.ativa;
      if (!h) return '';
      // A corrente e o portão chegam os dois como `pre_requisito`; o que os separa é o `requer`.
      if (h.requer) {
        return this.$t('skills.tier.requiresSkill', {
          nome: this.$t(`skills.catalog.${h.requer}.name`),
        });
      }
      if (h.motivoBloqueio === 'pre_requisito') {
        return this.$t('skills.tier.requires', {
          pontos: h.exigido ?? 0,
          arvore: this.$t(`skills.trees.${h.arvore}`),
        });
      }
      if (h.tier === 4) return this.$t('skills.tier.capstone');
      return h.tier === 1 ? this.$t('skills.tier.open') : this.$t('skills.tier.unlocked');
    },
    /**
     * O motivo vai DENTRO do botão, que o corta com reticências numa linha só (é o contrato do
     * primitivo: "Nível 10 necessário", não uma frase). A explicação inteira fica na linha do
     * tier, acima, e no aviso de erro.
     */
    motivoDoBloqueio() {
      const h = this.ativa;
      if (!h?.motivoBloqueio) return '';
      if (h.requer) {
        return this.$t('skills.blocked.requer', {
          nome: this.$t(`skills.catalog.${h.requer}.name`),
        });
      }
      return this.$t(`skills.blocked.${h.motivoBloqueio}`, {
        exigido: h.exigido ?? 0,
        custo: h.custoDoProximo ?? 0,
        capstone: this.capstone ? this.$t(`skills.catalog.${this.capstone}.name`) : '',
      });
    },
    motivoDoReset() {
      if (this.pontos.alocados === 0) return this.$t('skills.blocked.nada_alocado');
      return this.emCooldown ? this.$t('skills.blocked.cooldown') : '';
    },
    emCooldown() {
      return Boolean(this.reset.disponivelEm) && new Date(this.reset.disponivelEm).getTime() > this.agora;
    },
  },
  methods: {
    formatar(n) {
      return Number(n || 0).toLocaleString('pt-BR');
    },
    quando(iso) {
      return new Date(iso).toLocaleString(this.$i18n.locale);
    },
    nosDa(arvore) {
      return arvore.habilidades.map((h) => ({
        ...posicaoNoTrilho(h.ordem),
        id: h.id,
        rotulo: this.$t(`skills.catalog.${h.id}.name`),
        icone: ICONE[h.id],
        // O número só aparece onde há ponto: 36 zeros na tela seriam ruído, não informação.
        grau: h.nivel > 0 ? h.nivel : null,
        aceso: h.liberada,
        pleno: h.nivel > 0 && h.nivel >= h.nivelMaximo,
        /*
         * Nada de bruma aqui (09/09/2026).
         *
         * A corrente (D5) abre a árvore com DUAS habilidades: no primeiro acesso, 34 dos 36 nós
         * estariam trancados, e a bruma esconde o ícone — o jogador não veria o que há pela
         * frente justamente na hora de escolher em que árvore investir. O nó trancado fica à
         * vista e apagado; quem diz por que é o painel.
         */
      }));
    },
    /** Gasto contra o que a árvore custa fechada — os dois números vêm do servidor. */
    progressoDa(arvore) {
      return arvore.custoTotal ? Math.min(1, arvore.pontos / arvore.custoTotal) : 0;
    },
    async alocar(id) {
      this.alocando = id;
      this.erro = null;
      const r = await this.$store.dispatch('skills/alocar', id);
      if (!r?.ok) this.mostrarErro(r?.codigo);
      this.alocando = null;
    },
    async resetar() {
      this.resetando = true;
      this.erro = null;
      const r = await this.$store.dispatch('skills/resetar');
      if (!r?.ok) this.mostrarErro(r?.codigo);
      this.agora = Date.now();
      this.resetando = false;
    },
    mostrarErro(codigo) {
      this.erro = codigo || 'erro';
      setTimeout(() => {
        this.erro = null;
      }, 4000);
    },
  },
  async mounted() {
    this.agora = Date.now();
    await this.$store.dispatch('skills/carregar');
    if (!this.ativa) this.habilidadeAtiva = this.arvores[0]?.habilidades?.[0]?.id ?? null;
  },
};
</script>

<style scoped>
.arvores {
  display: flex;
  flex-direction: column;
  gap: var(--e-4);
}

.arvores__quadro {
  display: grid;
  gap: var(--e-4);
}

/* No desktop: o núcleo e o painel numa coluna à esquerda, os trilhos na outra. O painel fica
 * grudado (`sticky`) porque a escolha acontece no trilho e o efeito se lê no painel — os dois
 * precisam estar na tela ao mesmo tempo. É o breakpoint `desktop` dos quatro (UI-08). */
@media (min-width: 900px) {
  .arvores__quadro {
    grid-template-columns: 300px minmax(0, 1fr);
    align-items: start;
  }

  .arvores__nucleo {
    grid-column: 1;
    grid-row: 1;
  }

  .arvores__trilhos {
    grid-column: 2;
    grid-row: 1 / span 2;
  }

  .arvores__lado {
    grid-column: 1;
    grid-row: 2;
    position: sticky;
    top: var(--e-4);
  }
}

/* O núcleo: papel escuro, como o centro da roda. */
.arvores__nucleo {
  text-align: center;
  color: var(--papel-luz);
  text-shadow: 1px 1px 0 var(--agua-abissal);
}

.arvores__pontos {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--e-2);
  margin: 0;
  color: var(--ouro-claro);
}

.arvores__pontos-valor {
  font-size: var(--f-6);
}

.arvores__alocados,
.arvores__capstone {
  margin: var(--e-1) 0 0;
}

.arvores__capstone {
  color: var(--ouro-claro);
}

/* A espinha à esquerda: as três árvores saem do mesmo núcleo, e o traço é o que diz isso. Fica na
 * borda do container de rolagem, então ele NÃO rola junto com os trilhos. */
.arvores__trilhos {
  min-width: 0;
  border-left: 3px solid var(--madeira-sombra);
  padding-left: var(--e-3);
}

.arvores__pista {
  overflow-x: auto;
  display: flex;
  flex-direction: column;
  gap: var(--e-2);
}

.arvores__nome {
  /* Grudado à esquerda: rolando o trilho no telefone, o nome da árvore continua à vista. */
  position: sticky;
  left: 0;
  width: fit-content;
  display: flex;
  align-items: center;
  gap: var(--e-2);
  margin: 0;
  color: var(--papel-luz);
  text-shadow: 1px 1px 0 var(--agua-abissal);
}

.arvores__nome-pontos {
  color: var(--ouro-claro);
}

/* O total é o horizonte, não o número que importa agora: fica mais apagado. */
.arvores__nome-total {
  color: var(--tinta-fraca);
}

.arvores__carregando {
  max-width: 100%;
}

.arvores__arvore-da-ativa {
  color: var(--tinta-fraca);
  margin: 0 0 var(--e-1);
}

.arvores__descricao {
  color: var(--tinta-fraca);
  margin: 0 0 var(--e-2);
}

.arvores__tier {
  color: var(--tinta-fraca);
  margin: 0 0 var(--e-3);
}

.arvores__efeitos {
  display: flex;
  align-items: center;
  gap: var(--e-3);
  margin: 0 0 var(--e-4);
}

.arvores__efeito {
  flex: 1;
  min-width: 0;
}

.arvores__efeito dt {
  color: var(--tinta-fraca);
}

.arvores__efeito dd {
  margin: 0;
  font-size: var(--f-5);
  color: var(--tinta);
}

.arvores__efeito-proximo {
  color: var(--cor-bom);
}

.arvores__seta {
  flex-shrink: 0;
  opacity: 0.6;
}

/* O motivo do bloqueio é desenhado ABAIXO do botão, fora da caixa dele (`position: absolute` no
 * primitivo). Sem esta folga a moldura o corta pela metade — foi o que apareceu no primeiro
 * screenshot da tela. */
.arvores__acao {
  background: none;
  padding: 0 0 var(--e-4);
}

.arvores__reset {
  margin-top: var(--e-4);
}

.arvores__cooldown {
  color: var(--tinta-fraca);
  margin: 0 0 var(--e-2);
}

.arvores__erro {
  margin: 0 auto;
  max-width: 420px;
  background: var(--papel);
  color: var(--cor-ruim);
  font-family: var(--fonte-corpo);
  text-align: center;
  padding: var(--e-2);
  border: 2px solid var(--cor-ruim);
}
</style>
